// src/pages/Offers.js
import React, { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { offerService } from '../services/offerService';
import { employeeService } from '../services/employeeService';
import { couponService } from '../services/couponService';
import './Offers.css';

// 🌈 CATEGORIES D'OFFRES
const OFFER_CATEGORIES = [
  { id: 'financial', name: 'Support financier', icon: '💰', color: '#4ecdc4' },
  { id: 'family', name: 'Famille & éducation', icon: '👨‍👩‍👧‍👦', color: '#ff9f43' },
  { id: 'shopping', name: 'Achats courants', icon: '🛍️', color: '#a29bfe' },
  { id: 'leisure', name: 'Voyages & loisirs', icon: '🌴', color: '#1dd1a1' },
  { id: 'health', name: 'Santé', icon: '🏥', color: '#48dbfb' },
  { id: 'food', name: 'Restauration', icon: '🍽️', color: '#ff6b6b' },
  { id: 'religious', name: 'Vie spirituelle', icon: '🕋', color: '#6c5ce7' },
  { id: 'wellness', name: 'Sport & bien-être', icon: '🧘', color: '#feca57' },
  { id: 'training', name: 'Formation pro', icon: '💼', color: '#10ac84' },
  { id: 'culture', name: 'Culture', icon: '🎭', color: '#ee5253' },
];

// 🎯 TYPES D'OFFRES
const OFFER_TYPES = [
  { id:'discount',     name:'Réduction',     icon:'🏷️', color:'#667eea' },
  { id:'voucher',      name:'Bon / Coupon',  icon:'🧾', color:'#feca57' },
  { id:'cash_support', name:'Prise en charge',icon:'🏛️', color:'#00b894' },
  { id:'credit',       name:'Crédit / Paiement', icon:'💳', color:'#54a0ff' },
  { id:'gift',         name:'Cadeau',        icon:'🎁', color:'#ff9ff3' },
  { id:'access',       name:'Accès VIP',     icon:'🔑', color:'#5f27cd' },
  { id:'event',        name:'Événement',     icon:'🎟️', color:'#48dbfb' },
  { id:'logistics',    name:'Livraison / Promo', icon:'🚚', color:'#96ceb4' },
];

// 🔧 CONFIGURATION DES ACTIONS PAR TYPE
const OFFER_SYSTEMS_CONFIG = {
  voucher: { hasCoupons: true },
  access: { hasCoupons: true },
  gift: { hasCoupons: true },
  discount: { hasCoupons: false },
  logistics: { hasCoupons: false },
  event: { hasCoupons: false },
  cash_support: { hasCoupons: false },
  credit: { hasCoupons: false }
};

// 🎫 Composant pour afficher les statistiques réelles des coupons
const CouponStats = ({ offerId }) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        setLoading(true);
        const couponsData = await couponService.getAdminCoupons(offerId);
        setCoupons(couponsData.data || couponsData || []);
      } catch (err) {
        setError('Erreur lors du chargement des coupons');
        console.error('Erreur fetch coupons:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCoupons();
  }, [offerId]);

  const stats = {
    total: coupons.length,
    used: coupons.filter(c => c.status === 'used').length,
    active: coupons.filter(c => c.status === 'active').length,
    expired: coupons.filter(c => c.status === 'expired').length
  };

  if (loading) return <span>⏳ Chargement...</span>;
  if (error) return <span className="error-text">{error}</span>;

  return (
    <div className="coupon-stats-inline">
      <span className="stat-badge total">📊 {stats.total}</span>
      <span className="stat-badge active">🟢 {stats.active}</span>
      <span className="stat-badge used">✅ {stats.used}</span>
      <span className="stat-badge expired">🔴 {stats.expired}</span>
    </div>
  );
};

// 🎨 Composant Modal pour les détails
const OfferDetailsModal = ({ offer, employeeNames, onClose }) => {
  if (!offer) return null;

  const categoryInfo = OFFER_CATEGORIES.find(cat => cat.id === offer.category) || OFFER_CATEGORIES[0];
  const typeInfo = OFFER_TYPES.find(type => type.id === offer.type) || OFFER_TYPES[0];

  const getExclusivityType = () => {
    if (!offer.isExclusive) return { type: 'public', label: '🌍 Publique', description: 'Accessible à tous les employés', badgeColor: '#10b981' };
    if (offer.isEmployeeExclusive) return { type: 'employee', label: '👤 Exclusif employés', description: 'Réservé à certains employés spécifiques', badgeColor: '#8a2be2' };
    return { type: 'company', label: '🏢 Exclusif entreprise', description: 'Accessible à tous les employés de votre entreprise', badgeColor: '#007bff' };
  };

  const exclusivity = getExclusivityType();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Détails de l'offre</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="exclusivity-banner" style={{ backgroundColor: exclusivity.badgeColor + '15' }}>
            <div className="exclusivity-content">
              <div className="exclusivity-text">
                <h4>{exclusivity.label}</h4>
                <p>{exclusivity.description}</p>
              </div>
            </div>
          </div>

          {offer.imageUrl && (
            <div className="offer-image-section">
              <div className="offer-image-container">
                <img src={offer.imageUrl} alt={offer.title} className="offer-detail-image" />
              </div>
            </div>
          )}

          <div className="details-section">
            <h3>Informations principales</h3>
            <div className="details-grid">
              <div className="detail-item"><label>Titre:</label><span>{offer.title}</span></div>
              <div className="detail-item"><label>Code offre:</label><span className="code-value">{offer.codeOffre}</span></div>
              <div className="detail-item"><label>Catégorie:</label><span>{categoryInfo.icon} {categoryInfo.name}</span></div>
              <div className="detail-item"><label>Type:</label><span>{typeInfo.icon} {typeInfo.name}</span></div>
              {offer.discount && <div className="detail-item"><label>Avantage:</label><span className="discount-highlight">{offer.discount}</span></div>}
              <div className="detail-item"><label>Statut:</label><span className={`status ${offer.isActive ? 'active' : 'inactive'}`}>{offer.isActive ? '🟢 Active' : '🔴 Inactive'}</span></div>
            </div>
          </div>

          <div className="details-section">
            <h3>Description</h3>
            <p className="full-description">{offer.description}</p>
          </div>

          <div className="details-section">
            <h3>Partenaire</h3>
            <div className="details-grid">
              <div className="detail-item"><label>Nom:</label><span>{offer.partnerName}</span></div>
            </div>
          </div>

          {offer.hasCoupons && (
            <div className="details-section">
              <h3>Gestion des coupons</h3>
              <div className="details-grid">
                <div className="detail-item"><label>Système:</label><span>🎫 Activé</span></div>
                <CouponStats offerId={offer._id} />
              </div>
            </div>
          )}

          {(offer.employerContribution > 0 || offer.maxAmount > 0) && (
            <div className="details-section">
              <h3>Contribution employeur</h3>
              <div className="details-grid">
                {offer.employerContribution > 0 && <div className="detail-item"><label>Contribution:</label><span>{offer.employerContribution}%</span></div>}
                {offer.maxAmount > 0 && <div className="detail-item"><label>Montant max:</label><span>{offer.maxAmount} DA</span></div>}
              </div>
            </div>
          )}

          <div className="details-section">
            <h3>Dates</h3>
            <div className="details-grid">
              <div className="detail-item"><label>Début:</label><span>{new Date(offer.startDate).toLocaleDateString('fr-FR')}</span></div>
              <div className="detail-item"><label>Fin:</label><span>{new Date(offer.endDate).toLocaleDateString('fr-FR')}</span></div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose}>Fermer</Button>
        </div>
      </div>
    </div>
  );
};

// 🎫 Composant Modal pour les coupons
const CouponsModal = ({ offer, coupons, onClose }) => {
  if (!offer) return null;
  const couponsList = Array.isArray(coupons) ? coupons : [];
  const stats = {
    total: couponsList.length,
    active: couponsList.filter(c => c.status === 'active').length,
    used: couponsList.filter(c => c.status === 'used').length,
    expired: couponsList.filter(c => c.status === 'expired').length
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🎫 Gestion des coupons</h2>
          <div className="modal-subtitle">{offer.title}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="coupons-stats">
            <div className="stat-item"><div className="stat-number total">{stats.total}</div><div className="stat-label">Total</div></div>
            <div className="stat-item"><div className="stat-number active">{stats.active}</div><div className="stat-label">Disponibles</div></div>
            <div className="stat-item"><div className="stat-number used">{stats.used}</div><div className="stat-label">Utilisés</div></div>
            <div className="stat-item"><div className="stat-number expired">{stats.expired}</div><div className="stat-label">Expirés</div></div>
          </div>
          <div className="coupons-list">
            <h3>Détail des coupons</h3>
            {couponsList.length === 0 ? (
              <div className="empty-coupons"><div className="empty-icon">🎫</div><p>Aucun coupon généré</p></div>
            ) : (
              <div className="coupons-table">
                <div className="table-header">
                  <div className="col-code">Code</div><div className="col-status">Statut</div><div className="col-user">Utilisateur</div><div className="col-date">Utilisation</div><div className="col-expiry">Expiration</div>
                </div>
                <div className="table-body">
                  {couponsList.map((coupon) => (
                    <div key={coupon._id} className={`table-row ${coupon.status}`}>
                      <div className="col-code"><span className="coupon-code">{coupon.code}</span></div>
                      <div className="col-status"><span className={`status-badge ${coupon.status}`}>{coupon.status === 'active' ? '🟢 Actif' : coupon.status === 'used' ? '✅ Utilisé' : '🔴 Expiré'}</span></div>
                      <div className="col-user">{coupon.userId ? (coupon.user?.firstName || 'Utilisateur') : 'Non attribué'}</div>
                      <div className="col-date">{coupon.usedAt ? new Date(coupon.usedAt).toLocaleDateString('fr-FR') : '-'}</div>
                      <div className="col-expiry">{new Date(coupon.expiresAt).toLocaleDateString('fr-FR')}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <Button variant="primary" onClick={onClose}>Fermer</Button>
        </div>
      </div>
    </div>
  );
};

// Composant principal Offers
const Offers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedOfferType, setSelectedOfferType] = useState('all');
  const [selectedExclusivity, setSelectedExclusivity] = useState('all');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectedCoupons, setSelectedCoupons] = useState(null);
  const [employeeNames, setEmployeeNames] = useState({});
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await offerService.getAdminOffers({
        category: selectedCategory !== 'all' ? selectedCategory : null,
        type: selectedOfferType !== 'all' ? selectedOfferType : null
      });
      setOffers(data || []);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des offres');
      console.error('Erreur fetch offers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, [selectedCategory, selectedOfferType]);

  const getCategoryInfo = (categoryId) => {
    return OFFER_CATEGORIES.find(cat => cat.id === categoryId) || OFFER_CATEGORIES[0];
  };

  const getExclusivityInfo = (offer) => {
    if (!offer.isExclusive) return { type: 'public', label: '🌍 Publique', color: '#10b981' };
    if (offer.isEmployeeExclusive) return { type: 'employee', label: '👤 Exclusif employés', color: '#8a2be2' };
    return { type: 'company', label: '🏢 Exclusif entreprise', color: '#007bff' };
  };

  useEffect(() => {
    const fetchEmployeeNamesForExclusiveOffers = async () => {
      const exclusiveEmployeeOffers = offers.filter(offer => 
        offer.isExclusive && offer.isEmployeeExclusive && offer.forEmployees?.length > 0
      );
      if (exclusiveEmployeeOffers.length === 0) {
        setEmployeeNames({});
        return;
      }
      try {
        setLoadingEmployees(true);
        const response = await employeeService.getEmployees();
        const allEmployees = response.employees || response.data || response || [];
        const employeeMap = new Map();
        allEmployees.forEach(emp => {
          const name = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || emp.employeeId || `Employé ${emp._id?.substring(0, 8) || 'N/A'}`;
          employeeMap.set(emp._id, { id: emp._id, name: name, employeeId: emp.employeeId, email: emp.email });
        });
        const employeeData = exclusiveEmployeeOffers.reduce((acc, offer) => {
          acc[offer._id] = offer.forEmployees.map(employeeId => {
            const employee = employeeMap.get(employeeId);
            return employee || { id: employeeId, name: `Employé (${employeeId.substring(0, 8)})`, notFound: true };
          });
          return acc;
        }, {});
        setEmployeeNames(employeeData);
      } catch (error) {
        console.error("❌ Erreur chargement noms employés:", error);
        const fallbackData = exclusiveEmployeeOffers.reduce((acc, offer) => {
          acc[offer._id] = offer.forEmployees.map(id => ({ id: id, name: `${offer.forEmployees.length} employé(s)`, isFallback: true }));
          return acc;
        }, {});
        setEmployeeNames(fallbackData);
      } finally {
        setLoadingEmployees(false);
      }
    };
    const timerId = setTimeout(() => {
      if (offers.length > 0) fetchEmployeeNamesForExclusiveOffers();
      else setEmployeeNames({});
    }, 300);
    return () => clearTimeout(timerId);
  }, [offers]);

  const getTypeInfo = (typeId) => {
    return OFFER_TYPES.find(type => type.id === typeId) || OFFER_TYPES[0];
  };

  const getOfferSystem = (offerType) => {
    return OFFER_SYSTEMS_CONFIG[offerType] || { hasCoupons: false };
  };

  // FILTRAGE FRONTEND (inclut l'exclusivité)
  const filteredOffers = offers.filter(offer => {
    const categoryMatch = selectedCategory === 'all' || offer.category === selectedCategory;
    const typeMatch = selectedOfferType === 'all' || offer.type === selectedOfferType;
    
    // Filtre exclusivité côté frontend
    let exclusivityMatch = true;
    if (selectedExclusivity !== 'all') {
      if (selectedExclusivity === 'public') {
        exclusivityMatch = !offer.isExclusive;
      } else if (selectedExclusivity === 'company') {
        exclusivityMatch = offer.isExclusive && !offer.isEmployeeExclusive;
      } else if (selectedExclusivity === 'employee') {
        exclusivityMatch = offer.isExclusive && offer.isEmployeeExclusive;
      }
    }
    
    return categoryMatch && typeMatch && exclusivityMatch;
  });

  const handleCloseCouponsModal = () => setSelectedCoupons(null);
  const handleViewCoupons = async (offerId) => {
    try {
      setLoading(true);
      const offer = offers.find(o => o._id === offerId);
      if (!offer) { setError('Offre non trouvée'); return; }
      const couponsData = await couponService.getAdminCoupons(offerId);
      setSelectedCoupons({ offer: offer, coupons: couponsData.data || couponsData || [] });
    } catch (err) {
      setError(err.message || 'Erreur lors de la récupération des coupons');
    } finally {
      setLoading(false);
    }
  };
  const handleViewDetails = (offerId) => {
    const offer = offers.find(o => o._id === offerId);
    if (offer) setSelectedOffer(offer);
  };
  const handleCloseModal = () => setSelectedOffer(null);

  if (loading) {
    return (
      <div className="offers">
        <div className="page-header">
          <div className="page-title">
            <h1>Gestion des Offres</h1>
          </div>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="offers">
      <div className="page-header">
        <div className="page-title">
          <h1>Gestion des Offres</h1>
          <p>Consultez et gérez les offres disponibles ({filteredOffers.length} offre{filteredOffers.length > 1 ? 's' : ''})</p>
        </div>
        
        <div className="header-actions">
          <div className="filters">
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="filter-select">
              <option value="all">📋 Toutes les catégories</option>
              {OFFER_CATEGORIES.map(category => (
                <option key={category.id} value={category.id}>{category.icon} {category.name}</option>
              ))}
            </select>

            <select value={selectedOfferType} onChange={(e) => setSelectedOfferType(e.target.value)} className="filter-select">
              <option value="all">🎁 Tous les types</option>
              {OFFER_TYPES.map(type => (
                <option key={type.id} value={type.id}>{type.icon} {type.name}</option>
              ))}
            </select>

            <select value={selectedExclusivity} onChange={(e) => setSelectedExclusivity(e.target.value)} className="filter-select">
              <option value="all">👑 Toutes les offres</option>
              <option value="public">🌍 Publique</option>
              <option value="company">🏢 Exclusif entreprise</option>
              <option value="employee">👤 Exclusif employés</option>
            </select>
          </div>
        </div>
      </div>

      <div className="offers-stats">
        <Card className="stat-card">
          <div className="stat-content">
            <div className="stat-icon">📦</div>
            <div className="stat-info">
              <div className="stat-number">{filteredOffers.length}</div>
              <div className="stat-label">Total offres</div>
            </div>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-content">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <div className="stat-number">{offers.filter(o => o.isActive).length}</div>
              <div className="stat-label">Offres actives</div>
            </div>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-content">
            <div className="stat-icon">👑</div>
            <div className="stat-info">
              <div className="stat-number">{offers.filter(o => o.isExclusive).length}</div>
              <div className="stat-label">Offres exclusives</div>
            </div>
          </div>
        </Card>
      </div>

      {error && (
        <Card className="error-card" padding="md">
          <div className="error-message">❌ {error}</div>
        </Card>
      )}

      <div className="offers-table-container">
        <table className="offers-table">
          <thead>
            <tr>
              <th>Offre</th>
              <th>Catégorie</th>
              <th>Type</th>
              <th>Avantage</th>
              <th>Partenaire</th>
              <th>Exclusivité</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOffers.map(offer => {
              const categoryInfo = getCategoryInfo(offer.category);
              const typeInfo = getTypeInfo(offer.type);
              const offerSystem = getOfferSystem(offer.type);
              const hasCoupons = offerSystem.hasCoupons && offer.hasCoupons;
              const exclusivityInfo = getExclusivityInfo(offer);

              return (
                <tr key={offer._id}>
                  <td data-label="Offre">
                    <div className="offer-cell">
                      <strong>{offer.title}</strong>
                      <small className="offer-code">{offer.codeOffre}</small>
                    </div>
                   </td>
                  <td data-label="Catégorie">
                    <span className="category-badge" style={{ backgroundColor: `${categoryInfo.color}15`, color: categoryInfo.color }}>
                      {categoryInfo.icon} {categoryInfo.name}
                    </span>
                   </td>
                  <td data-label="Type">
                    <span className="type-badge" style={{ backgroundColor: typeInfo.color, color: 'white' }}>
                      {typeInfo.icon} {typeInfo.name}
                    </span>
                   </td>
                  <td data-label="Avantage">
                    <span className="discount-value">{offer.discount || '-'}</span>
                   </td>
                  <td data-label="Partenaire">
                    <span className="partner-name">{offer.partnerName}</span>
                   </td>
                  <td data-label="Exclusivité">
                    <span className="exclusivity-badge-table" style={{ backgroundColor: `${exclusivityInfo.color}15`, color: exclusivityInfo.color }}>
                      {exclusivityInfo.label}
                    </span>
                   </td>
                  <td data-label="Statut">
                    <span className={`status-badge ${offer.isActive ? 'active' : 'inactive'}`}>
                      {offer.isActive ? '🟢 Active' : '🔴 Inactive'}
                    </span>
                   </td>
                  <td data-label="Actions">
                    <div className="actions-cell">
                      <button className="action-btn view" onClick={() => handleViewDetails(offer._id)} title="Voir détails">👁️</button>
                      {hasCoupons && (
                        <button className="action-btn coupons" onClick={() => handleViewCoupons(offer._id)} title="Voir coupons">🎫</button>
                      )}
                    </div>
                   </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredOffers.length === 0 && !loading && (
        <Card className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3>Aucune offre trouvée</h3>
          <Button variant="primary" onClick={() => {
            setSelectedCategory('all');
            setSelectedOfferType('all');
            setSelectedExclusivity('all');
          }}>
            📋 Voir toutes les offres
          </Button>
        </Card>
      )}

      {selectedOffer && (
        <OfferDetailsModal offer={selectedOffer} employeeNames={employeeNames} onClose={handleCloseModal} />
      )}

      {selectedCoupons && (
        <CouponsModal offer={selectedCoupons.offer} coupons={selectedCoupons.coupons} onClose={handleCloseCouponsModal} />
      )}
    </div>
  );
};

export default Offers;